import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Check,
  ChevronRight,
  Circle,
  CircleCheck,
  CircleDashed,
  Contact,
  HeartHandshake,
  Info,
  Key,
  Loader2,
  Lock,
  MessageCircle,
  PenLine,
  Plus,
  Sparkles,
  Sun,
  Trash2,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LogoMark } from "@/components/logo";
import { NoLiteratureNote, StepReadings } from "@/components/recovery/step-readings";
import {
  ApiError,
  addStep8Row,
  deleteStep8List,
  deleteStep8Row,
  getStep,
  getStep8Rows,
  startStep,
  updateStep,
  updateStep8Row,
  type Step8Person,
  type Step8Row,
  type Step8Willingness,
  type StepData,
} from "@/lib/api";
import { decryptRow, encryptRow } from "@/lib/step8-crypto";
import { shortDate } from "@/lib/format";

/**
 * Step 8 — the living list of people harmed (Step 8 PRD + design canvas).
 * Like Step 4 it breaks the reflective template: a row-based list built one
 * person at a time, END-TO-END ENCRYPTED on this device
 * (src/lib/step8-crypto.ts), with no sponsor visibility and no AI at all.
 *
 * Two halves, kept distinct (§2): the list, and the willingness. Willingness
 * has its own screen you can open from a row, because becoming willing is the
 * actual work — finishing the list is not finishing the step.
 *
 * Unlike everything else in the app, rows are editable and individually
 * deletable (§5). Nothing is read from Step 4 (§3): that inventory is
 * encrypted and may already be deleted.
 */

/** The three honest willingness states (PRD §4; owner may re-word). */
const WILL: Record<
  Step8Willingness,
  { label: string; help: string; icon: typeof Circle }
> = {
  not: {
    label: "Not willing yet",
    help: "Honest, and a fine place to be.",
    icon: Circle,
  },
  working: {
    label: "Working on it",
    help: "Something is moving, slowly.",
    icon: CircleDashed,
  },
  willing: {
    label: "Willing",
    help: "Ready to make it right when the time comes.",
    icon: CircleCheck,
  },
};
const WILL_KEYS: Step8Willingness[] = ["not", "working", "willing"];

const EMPTY: Step8Person = {
  who: "",
  harm: "",
  my_part: "",
  note: "",
  willingness: "not",
  flagged: false,
  // Step 9's fields ride on the same row; a new person starts with no amends
  // outcome, and Step 8's editor carries them through untouched.
  outcome: "notyet",
  outcome_note: "",
};

type Decrypted = Step8Row & { person: Step8Person | null }; // null = other device

type Screen =
  | { s: "overview" }
  | { s: "reading" }
  | { s: "list" }
  | { s: "row"; rowId?: string }
  | { s: "willingness"; rowId: string }
  | { s: "privacy" }
  | { s: "ready" }
  | { s: "close" }
  | { s: "deleted" };

function EncryptedTag() {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Lock className="h-[13px] w-[13px]" strokeWidth={2} /> Encrypted · only you
    </span>
  );
}

function Note({ icon: Icon = Sun, children }: { icon?: typeof Sun; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-primary/[0.06] px-4 py-3.5">
      <Icon className="mt-0.5 h-[18px] w-[18px] shrink-0 text-primary" strokeWidth={2} />
      <p className="text-[13px] leading-relaxed text-foreground">{children}</p>
    </div>
  );
}

/**
 * Deliberately NOT the check-in's green/yellow/red: reusing status colours
 * would make "Not willing yet" read as a red mark against a person. Shape and
 * label carry the state; the primary tint marks only the current selection.
 */
function WillPill({ state }: { state: Step8Willingness }) {
  const w = WILL[state];
  const Icon = w.icon;
  const on = state === "willing";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
        on
          ? "border-primary bg-primary/[0.06] text-primary"
          : "border-border bg-muted text-muted-foreground"
      }`}
    >
      <Icon className="h-[13px] w-[13px]" strokeWidth={2} /> {w.label}
    </span>
  );
}

function WillPicker({
  value,
  onChange,
}: {
  value: Step8Willingness;
  onChange: (v: Step8Willingness) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {WILL_KEYS.map((k) => {
        const w = WILL[k];
        const Icon = w.icon;
        const on = k === value;
        return (
          <button
            key={k}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(k)}
            className={`flex min-h-[44px] items-start gap-3 rounded-lg border px-4 py-3.5 text-left transition-colors ${
              on ? "border-2 border-primary bg-primary/[0.06]" : "border-border"
            }`}
          >
            <span className={`mt-0.5 ${on ? "text-primary" : "text-muted-foreground"}`}>
              <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
            </span>
            <span>
              <span className="block text-sm font-medium text-foreground">{w.label}</span>
              <span className="block text-xs text-muted-foreground">{w.help}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function Step8() {
  const navigate = useNavigate();
  const [data, setData] = useState<StepData | null>(null);
  const [rows, setRows] = useState<Decrypted[]>([]);
  const [screen, setScreen] = useState<Screen>({ s: "overview" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [readOn, setReadOn] = useState("");
  // Which delete is being confirmed: the whole list, or one person.
  const [confirming, setConfirming] = useState<{ scope: "list" } | { scope: "row"; id: string } | null>(
    null
  );

  async function load() {
    try {
      const [d, r] = await Promise.all([getStep(8), getStep8Rows()]);
      setData(d);
      setReadOn(d.work?.reading_read_on ?? "");
      setRows(
        await Promise.all(
          r.rows.map(async (row) => {
            const dec = await decryptRow(row.ciphertext);
            return {
              ...row,
              person: dec ? ({ ...EMPTY, ...(JSON.parse(dec.v ?? "{}") as Step8Person) }) : null,
            };
          })
        )
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else setError("Couldn't load Step 8.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = rows.length;
  const foreignRows = rows.some((r) => r.person === null);
  const lastAdded = total ? rows[total - 1].created_at : null;
  // A quiet sense of the list's shape — never a score, never a bar (§4).
  const shape = useMemo(() => {
    const c: Record<Step8Willingness, number> = { not: 0, working: 0, willing: 0 };
    let flagged = 0;
    for (const r of rows) {
      if (!r.person) continue;
      c[r.person.willingness] = (c[r.person.willingness] ?? 0) + 1;
      if (r.person.flagged) flagged++;
    }
    return { ...c, flagged };
  }, [rows]);

  async function begin() {
    setBusy(true);
    try {
      if (!data?.work) await startStep(8);
      await updateStep(8, { current_phase: data?.work ? "list" : "reading" });
      await load();
      setScreen(data?.work ? { s: "list" } : { s: "reading" });
    } catch {
      setError("Couldn't start Step 8.");
    } finally {
      setBusy(false);
    }
  }

  async function savePerson(rowId: string | undefined, person: Step8Person) {
    setBusy(true);
    try {
      const ciphertext = await encryptRow({ v: JSON.stringify(person) });
      if (rowId) {
        const updated = await updateStep8Row(rowId, ciphertext);
        setRows((rs) => rs.map((r) => (r.id === rowId ? { ...r, ...updated, person } : r)));
      } else {
        const created = await addStep8Row(ciphertext);
        setRows((rs) => [...rs, { ...created, person }]);
      }
      setScreen({ s: "list" });
    } catch {
      setError("Couldn't save. Nothing you wrote left this screen — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function removePerson(rowId: string) {
    setBusy(true);
    try {
      await deleteStep8Row(rowId);
      setRows((rs) => rs.filter((r) => r.id !== rowId));
      setConfirming(null);
      setScreen({ s: "list" });
    } catch {
      setError("Couldn't remove that row. Your list is unchanged.");
    } finally {
      setBusy(false);
    }
  }

  async function deleteEverything() {
    setBusy(true);
    try {
      await deleteStep8List();
      setRows([]);
      setConfirming(null);
      setScreen({ s: "deleted" });
    } catch {
      setError("Couldn't delete. Your list is unchanged.");
    } finally {
      setBusy(false);
    }
  }

  async function markReady() {
    setBusy(true);
    try {
      await updateStep(8, { status: "ready", ready_to_talk: true, current_phase: "close" });
      await load();
      setScreen({ s: "close" });
    } catch {
      setError("Couldn't mark Step 8 ready.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={() => navigate("/steps")}>Back to Steps</Button>
      </div>
    );
  }
  if (!data) return null;
  if (!data.available) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <Lock className="h-8 w-8 text-muted-foreground" strokeWidth={2} />
        <div>
          <h1 className="text-xl font-semibold text-foreground">Step 8 isn't open yet</h1>
          <p className="mx-auto mt-2 max-w-[300px] text-sm leading-relaxed text-muted-foreground">
            It opens when you mark Step 7 ready. There's no rush to get there.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/steps")}>Back to Steps</Button>
      </div>
    );
  }

  const { step } = data;
  const header = (title?: string, opts?: { back?: () => void; saved?: boolean }) => (
    <div className="flex items-center gap-2 px-4 pb-2 pt-6">
      <Button
        variant="ghost"
        size="icon"
        aria-label="Back"
        onClick={opts?.back ?? (() => navigate("/steps"))}
      >
        <ArrowLeft className="h-5 w-5" strokeWidth={2} />
      </Button>
      <span className="flex-1 text-sm font-semibold text-foreground">{title}</span>
      {opts?.saved ? (
        <span className="pr-2 text-xs text-muted-foreground">Saved</span>
      ) : (
        <span className="pr-2"><EncryptedTag /></span>
      )}
    </div>
  );

  /* ------------------------------ overview ------------------------------ */
  if (screen.s === "overview") {
    const resumed = !!data.work && total > 0;
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header()}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Eight{step.principle ? ` · ${step.principle}` : ""}
            </p>
            <h1 className="mt-1 text-2xl font-bold text-foreground">Step 8</h1>
          </div>
          <p className="text-[15px] leading-relaxed text-foreground">{step.intro_text}</p>
          {resumed ? (
            <Card>
              <CardContent className="flex flex-col gap-2 px-4 py-3.5">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  Where you left off
                </p>
                <p className="flex items-center gap-2 text-sm text-foreground">
                  <PenLine className="h-3.5 w-3.5" strokeWidth={2} />
                  {total} {total === 1 ? "person" : "people"} listed
                  {lastAdded ? ` · last added ${shortDate(lastAdded)}` : ""}
                </p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Willingness moves at its own pace. You can change any of it whenever it
                  changes in you.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex flex-col gap-2.5">
                {(
                  [
                    [Users, "The list", "Who was harmed, what happened, and your part in it."],
                    [
                      HeartHandshake,
                      "The willingness",
                      "Kept separately, per person, and it's allowed to change.",
                    ],
                  ] as [typeof Users, string, string][]
                ).map(([Icon, t, d]) => (
                  <div key={t} className="flex items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Icon className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{t}</p>
                      <p className="text-xs leading-snug text-muted-foreground">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
              {step.privacy_note && <Note icon={Lock}>{step.privacy_note}</Note>}
              {step.supporting_note && <Note>{step.supporting_note}</Note>}
            </>
          )}
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            Nothing here is sent anywhere, and nothing is done to anyone. Amends themselves are
            Step 9, in the real world, with your sponsor.
          </p>
          <div className="mt-auto flex flex-col gap-2 pt-4">
            <Button size="xl" fullWidth disabled={busy} onClick={begin}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              ) : resumed ? (
                "Continue Your List"
              ) : data.work ? (
                "Continue Step 8"
              ) : (
                "Begin Step 8"
              )}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => setScreen({ s: "privacy" })}>
              How This Is Stored
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------ reading ------------------------------ */
  if (screen.s === "reading") {
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Step 8", { back: () => setScreen({ s: "overview" }), saved: true })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-3.5 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Eight · Reading
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">The reading</h1>
          </div>
          <StepReadings citations={data.citations} />
          {data.citations.length === 0 && <NoLiteratureNote />}
          {step.reading_note && <Note icon={BookOpen}>{step.reading_note}</Note>}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">
              When did you read it?{" "}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <Input
              placeholder="e.g. July 21"
              value={readOn}
              onChange={(e) => setReadOn(e.target.value)}
            />
          </div>
          <div className="mt-auto flex flex-col gap-2 pt-4">
            <Button
              size="xl"
              fullWidth
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await updateStep(8, {
                    reading_read_on: readOn.trim() || null,
                    reading_acknowledged: true,
                    current_phase: "list",
                  });
                  await load();
                  setScreen({ s: "list" });
                } catch {
                  setError("Couldn't save your progress. Please try again.");
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : "Continue"}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => navigate("/steps")}>
              Save &amp; Exit
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* -------------------------------- the list -------------------------------- */
  if (screen.s === "list") {
    const empty = total === 0;
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Step 8", { back: () => setScreen({ s: "overview" }) })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-3 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Your list
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">
              {empty
                ? "Start with one person."
                : total === 1
                  ? "One person on the list."
                  : `${total} people, so far.`}
            </h1>
          </div>
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            {empty
              ? "Whoever comes to mind first is the right place to begin. You can add more any day — there's no finishing today."
              : "No target and no order. Add someone whenever they come to mind, and change any of this whenever it changes."}
          </p>
          {foreignRows && (
            <Note icon={Key}>
              Some rows were written on another device and can't be read here — the key stays where
              they were written. They're still safe, and still only yours.
            </Note>
          )}

          {empty ? (
            <div className="mt-1 flex flex-col items-center gap-2.5 rounded-xl border border-dashed border-border px-5 py-7 text-center">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Users className="h-5 w-5" strokeWidth={2} />
              </span>
              <p className="text-sm font-medium text-foreground">Nobody listed yet</p>
              <p className="max-w-[240px] text-[12.5px] leading-relaxed text-muted-foreground">
                Most people build this over weeks, with their sponsor. One name is a real start.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {rows.map((r) => (
                <Card key={r.id} interactive onClick={() => setScreen({ s: "row", rowId: r.id })}>
                  <CardContent className="flex items-center gap-3 px-4 py-3.5">
                    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {r.person
                          ? r.person.who.trim() || "Unnamed"
                          : "Written on another device"}
                      </p>
                      {r.person?.harm.trim() && (
                        <p className="truncate text-xs text-muted-foreground">{r.person.harm}</p>
                      )}
                      {r.person && (
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setScreen({ s: "willingness", rowId: r.id });
                            }}
                            aria-label={`Change willingness for ${r.person.who || "this person"}`}
                          >
                            <WillPill state={r.person.willingness} />
                          </button>
                          {r.person.flagged && (
                            <span className="inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
                              <MessageCircle className="h-3 w-3" strokeWidth={2} /> To talk through
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2} />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {total === 1 && <Note>That's one more than yesterday. Come back when the next person comes to mind.</Note>}

          {total > 0 && data.work?.status !== "ready" && data.work?.status !== "worked" && (
            <Button variant="ghost" fullWidth onClick={() => setScreen({ s: "ready" })}>
              I've Made My List
            </Button>
          )}

          <div className="mt-auto flex flex-col items-start gap-2.5 pt-5">
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="h-[13px] w-[13px]" strokeWidth={2} /> Encrypted on this device ·
              never shared with your sponsor
            </span>
            {total > 0 && (
              <button
                type="button"
                className="text-[13px] text-muted-foreground underline underline-offset-2"
                onClick={() => setConfirming({ scope: "list" })}
              >
                Delete My Step 8 List
              </button>
            )}
          </div>
          <div className="flex flex-col gap-2 pt-4">
            <Button size="xl" fullWidth onClick={() => setScreen({ s: "row" })}>
              <Plus className="h-4 w-4" strokeWidth={2} /> Add a Person
            </Button>
            <Button variant="ghost" fullWidth onClick={() => navigate("/steps")}>
              Back to Steps
            </Button>
          </div>
        </div>
        {confirming && deleteModal()}
      </div>
    );
  }

  /* ---------------------------- add / edit a row ---------------------------- */
  if (screen.s === "row") {
    const existing = screen.rowId ? rows.find((r) => r.id === screen.rowId) : undefined;
    return (
      <>
        <PersonEditor
          key={screen.rowId ?? "new"}
          initial={existing?.person ?? EMPTY}
          editing={!!existing}
          busy={busy}
          onBack={() => setScreen({ s: "list" })}
          onSave={(person) => void savePerson(screen.rowId, person)}
          onExit={() => navigate("/steps")}
          onRemove={
            existing ? () => setConfirming({ scope: "row", id: existing.id }) : undefined
          }
        />
        {confirming && deleteModal()}
      </>
    );
  }

  /* --------------------- willingness, on its own screen --------------------- */
  if (screen.s === "willingness") {
    const row = rows.find((r) => r.id === screen.rowId);
    if (!row?.person) {
      setScreen({ s: "list" });
      return null;
    }
    return (
      <WillingnessScreen
        key={row.id}
        person={row.person}
        since={row.updated_at}
        busy={busy}
        header={header}
        onBack={() => setScreen({ s: "list" })}
        onSave={(willingness) => void savePerson(row.id, { ...row.person!, willingness })}
      />
    );
  }

  /* ------------------------------ privacy ------------------------------ */
  if (screen.s === "privacy") {
    const facts: [typeof Lock, string, string][] = [
      [
        Lock,
        "Encrypted on this device",
        "Only ciphertext leaves your phone. There is no way for us to read a name, a harm, or a note.",
      ],
      [
        Users,
        "Never shared with your sponsor",
        "There is no sharing control for Step 8, on purpose. Amends work is talked about in person.",
      ],
      [
        Sparkles,
        "No AI, at all",
        "No suggested names, no categorising, no coaching on willingness. It couldn't read this if we wanted it to.",
      ],
      [
        Contact,
        "No contacts, email or phone",
        "Names are plain text you type. Nothing here can contact anyone.",
      ],
      [
        PenLine,
        "Yours to change and delete",
        "Rows can be edited or removed at any time. Unusual for this app — a working list should be allowed to change.",
      ],
    ];
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("How Step 8 is stored", {
          back: () => setScreen(data.work ? { s: "list" } : { s: "overview" }),
        })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <p className="text-[15px] leading-relaxed text-foreground">
            This list names people who never agreed to be in an app. It's held accordingly.
          </p>
          {facts.map(([Icon, t, d]) => (
            <div key={t} className="flex items-start gap-3">
              <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon className="h-[17px] w-[17px]" strokeWidth={2} />
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground">{t}</p>
                <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{d}</p>
              </div>
            </div>
          ))}
          <Note icon={Key}>
            Because only you hold the key, there's no way for us to recover this list if you lose
            access to this device. That's the trade for real privacy.
          </Note>
          <div className="mt-auto flex flex-col items-center gap-3 pt-4">
            <Button
              size="xl"
              fullWidth
              onClick={() => setScreen(data.work ? { s: "list" } : { s: "overview" })}
            >
              {data.work ? "Back to My List" : "Back to Step 8"}
            </Button>
            {total > 0 && (
              <button
                type="button"
                className="text-[13px] text-muted-foreground underline underline-offset-2"
                onClick={() => setConfirming({ scope: "list" })}
              >
                Delete My Step 8 List
              </button>
            )}
          </div>
        </div>
        {confirming && deleteModal()}
      </div>
    );
  }

  /* ------------------------------- ready ------------------------------- */
  if (screen.s === "ready") {
    return (
      <div className="flex min-h-dvh flex-col pb-8">
        {header("Step 8", { back: () => setScreen({ s: "list" }), saved: true })}
        <div className="animate-fade-rise flex flex-1 flex-col gap-4 px-6 pt-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Step Eight · Ready
            </p>
            <h1 className="mt-1 text-xl font-semibold text-foreground">
              Ready to take the list to your sponsor?
            </h1>
          </div>
          <p className="text-[15px] leading-relaxed text-foreground">
            Nothing is sent. Marking this ready just says you've made your list and you're willing
            to start — the list itself stays on your phone, and you read from it together.
          </p>
          <Card>
            <CardContent className="flex flex-col gap-2.5 p-4">
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                What you have
              </p>
              {/* Prose, not a tally — and never a bar to clear (§4). */}
              <p className="text-sm leading-relaxed text-foreground">
                {total} {total === 1 ? "person" : "people"}.{" "}
                {shape.willing > 0 && `${shape.willing} you're willing with`}
                {shape.willing > 0 && (shape.working > 0 || shape.not > 0) ? ", " : ""}
                {shape.working > 0 && `${shape.working} you're working on`}
                {shape.working > 0 && shape.not > 0 ? ", " : ""}
                {shape.not > 0 && `${shape.not} you're not willing about yet`}
                {shape.flagged > 0
                  ? ` — and ${shape.flagged} marked to talk through.`
                  : "."}
              </p>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Not a score, and not a bar to clear. Some people mark this ready with half the
                list still unwilling.
              </p>
            </CardContent>
          </Card>
          <div className="flex items-center gap-3 rounded-lg border-2 border-primary bg-primary/[0.06] px-4 py-3.5">
            <CircleCheck className="h-5 w-5 shrink-0 text-primary" strokeWidth={2} />
            <p className="text-sm font-medium text-foreground">Mark Step 8 ready</p>
          </div>
          <Note>
            The list keeps growing after this. People come to mind for years, and you add them
            then — being ready doesn't close it.
          </Note>
          <div className="mt-auto flex flex-col gap-2 pt-4">
            <Button size="xl" fullWidth disabled={busy} onClick={() => void markReady()}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              ) : (
                "Let My Sponsor Know"
              )}
            </Button>
            <Button variant="ghost" fullWidth onClick={() => setScreen({ s: "list" })}>
              Not Yet
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------- close ------------------------------- */
  if (screen.s === "close") {
    return (
      <div className="flex min-h-dvh flex-col px-6 pb-8 pt-6">
        <div className="animate-fade-rise flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <LogoMark className="h-14 w-14 animate-scale-in" />
          <h1 className="text-2xl font-bold text-foreground">You've marked Step 8 ready.</h1>
          <p className="mx-auto max-w-[300px] text-[15px] leading-relaxed text-muted-foreground">
            The list is still yours, still private, and still open — add to it whenever someone
            comes to mind. Step 9 happens out there, with your sponsor, a person at a time. The app
            just holds your place.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <Button size="xl" fullWidth onClick={() => navigate("/steps")}>Return to Steps</Button>
          <Button variant="ghost" fullWidth onClick={() => navigate("/checkin")}>
            Back to Today's Check-In
          </Button>
        </div>
      </div>
    );
  }

  /* ------------------------------ deleted ------------------------------ */
  return (
    <div className="flex min-h-dvh flex-col px-6 pb-8 pt-6">
      <div className="animate-fade-rise flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Check className="h-7 w-7" strokeWidth={2} />
        </span>
        <h1 className="text-[21px] font-bold text-foreground">Your Step 8 list is gone.</h1>
        <p className="mx-auto max-w-[290px] text-[15px] leading-relaxed text-muted-foreground">
          Deleted for good — it isn't in a trash folder anywhere. Your Step 8 progress stays
          marked, and you can start a new list whenever you want.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <Button size="xl" fullWidth onClick={() => setScreen({ s: "list" })}>
          Back to Step 8
        </Button>
        <Button variant="ghost" fullWidth onClick={() => navigate("/steps")}>
          Return to Steps
        </Button>
      </div>
    </div>
  );

  function deleteModal() {
    if (!confirming) return null;
    const row = confirming.scope === "row" ? rows.find((r) => r.id === confirming.id) : undefined;
    const who = row?.person?.who.trim();
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/45 p-3">
        <div className="flex w-full max-w-md flex-col gap-3 rounded-xl border border-border bg-card p-6 shadow-lg">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-status-red-bg text-status-red-text">
            <Trash2 className="h-5 w-5" strokeWidth={2} />
          </span>
          {confirming.scope === "row" ? (
            <>
              <h2 className="text-lg font-bold text-foreground">
                Remove {who || "this person"} from the list?
              </h2>
              <p className="text-sm leading-relaxed text-foreground">
                The row and everything written in it goes. You can add them again later if you
                want to — it just starts blank.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-lg font-bold text-foreground">Delete your whole Step 8 list?</h2>
              <p className="text-sm leading-relaxed text-foreground">
                Every person, every note, gone from this device. Your Step 8 progress stays
                marked; the writing doesn't come back.
              </p>
              <Note icon={Info}>
                Step 9 works from this list. Most people keep it until their amends are done — but
                it's yours, and you never have to justify deleting it.
              </Note>
            </>
          )}
          <div className="mt-1 flex flex-col gap-2">
            <Button
              variant="destructive"
              size="lg"
              fullWidth
              disabled={busy}
              onClick={() =>
                confirming.scope === "row"
                  ? void removePerson(confirming.id)
                  : void deleteEverything()
              }
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              ) : confirming.scope === "row" ? (
                "Remove"
              ) : (
                "Delete Everything"
              )}
            </Button>
            <Button variant="ghost" size="lg" fullWidth onClick={() => setConfirming(null)}>
              Keep It
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

/** The one reusable add-a-person screen, used for new rows and for editing. */
function PersonEditor({
  initial,
  editing,
  busy,
  onBack,
  onSave,
  onExit,
  onRemove,
}: {
  initial: Step8Person;
  editing: boolean;
  busy: boolean;
  onBack: () => void;
  onSave: (person: Step8Person) => void;
  onExit: () => void;
  onRemove?: () => void;
}) {
  const [person, setPerson] = useState<Step8Person>(initial);
  const set = <K extends keyof Step8Person>(k: K, v: Step8Person[K]) =>
    setPerson((p) => ({ ...p, [k]: v }));

  return (
    <div className="flex min-h-dvh flex-col pb-8">
      <div className="flex items-center gap-2 px-4 pb-2 pt-6">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <span className="flex-1 text-sm font-semibold text-foreground">
          {editing ? "Edit" : "Add a person"}
        </span>
        <span className="pr-2"><EncryptedTag /></span>
      </div>
      <div className="animate-fade-rise flex flex-1 flex-col gap-3.5 px-6 pt-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
            Step Eight · {editing ? "editing" : "new"}
          </p>
          <h1 className="mt-1 text-xl font-semibold text-foreground">
            {editing ? person.who.trim() || "This person" : "One person at a time"}
          </h1>
        </div>
        {!editing && (
          <Note icon={Lock}>
            Names are just text you type. No contacts, no email, no phone — nothing here can reach
            anyone.
          </Note>
        )}

        <div>
          <label className="mb-0.5 block text-[13px] font-semibold text-foreground">
            Who was harmed
          </label>
          <p className="mb-1.5 text-xs leading-snug text-muted-foreground">
            A name, or however you'd refer to them.
          </p>
          <Input
            placeholder="In your own words…"
            value={person.who}
            onChange={(e) => set("who", e.target.value)}
          />
        </div>
        <div>
          <label className="mb-0.5 block text-[13px] font-semibold text-foreground">
            What happened
          </label>
          <p className="mb-1.5 text-xs leading-snug text-muted-foreground">
            The harm, as plainly as you can say it.
          </p>
          <Textarea
            rows={3}
            placeholder="In your own words…"
            value={person.harm}
            onChange={(e) => set("harm", e.target.value)}
          />
        </div>
        <div>
          <label className="mb-0.5 block text-[13px] font-semibold text-foreground">My part</label>
          <p className="mb-1.5 text-xs leading-snug text-muted-foreground">
            Yours alone. Not anyone else's side of it.
          </p>
          <Textarea
            rows={3}
            placeholder="In your own words…"
            value={person.my_part}
            onChange={(e) => set("my_part", e.target.value)}
          />
        </div>

        <div>
          <p className="mb-0.5 text-[13px] font-semibold text-foreground">
            Where you are with making it right
          </p>
          <p className="mb-2 text-xs leading-snug text-muted-foreground">
            This changes. Come back and move it whenever it does.
          </p>
          <WillPicker value={person.willingness} onChange={(v) => set("willingness", v)} />
        </div>

        <button
          type="button"
          aria-pressed={person.flagged}
          onClick={() => set("flagged", !person.flagged)}
          className={`flex items-start gap-3 rounded-lg border px-4 py-3.5 text-left transition-colors ${
            person.flagged ? "border-2 border-primary bg-primary/[0.06]" : "border-border"
          }`}
        >
          <span className={`mt-0.5 ${person.flagged ? "text-primary" : "text-muted-foreground"}`}>
            {person.flagged ? (
              <CircleCheck className="h-[18px] w-[18px]" strokeWidth={2} />
            ) : (
              <Circle className="h-[18px] w-[18px]" strokeWidth={2} />
            )}
          </span>
          <span>
            <span className="block text-sm font-medium text-foreground">
              Talk this one through with my sponsor
            </span>
            <span className="block text-xs leading-snug text-muted-foreground">
              A marker for you, not a message. Nothing is sent.
            </span>
          </span>
        </button>

        <div>
          <label className="mb-1.5 block text-[13px] font-semibold text-foreground">
            A private note <span className="font-normal text-muted-foreground">(optional)</span>
          </label>
          <Textarea
            rows={2}
            placeholder="Anything else you want to keep with this one…"
            value={person.note}
            onChange={(e) => set("note", e.target.value)}
          />
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Leave anything blank and come back to it.
        </p>
        {onRemove && (
          <button
            type="button"
            className="self-start text-[13px] text-muted-foreground underline underline-offset-2"
            onClick={onRemove}
          >
            Remove This Person From the List
          </button>
        )}

        <div className="mt-auto flex flex-col gap-2 pt-4">
          <Button
            size="xl"
            fullWidth
            disabled={busy || !person.who.trim()}
            onClick={() => onSave(person)}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
            ) : editing ? (
              "Update"
            ) : (
              "Save This Person"
            )}
          </Button>
          <Button variant="ghost" fullWidth onClick={onExit}>
            Save &amp; Exit
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Changing willingness from a row, without reopening the whole form — the
 * second half of the step, given its own screen (§2).
 */
function WillingnessScreen({
  person,
  since,
  busy,
  header,
  onBack,
  onSave,
}: {
  person: Step8Person;
  since: string;
  busy: boolean;
  header: (title?: string, opts?: { back?: () => void; saved?: boolean }) => React.ReactNode;
  onBack: () => void;
  onSave: (willingness: Step8Willingness) => void;
}) {
  const [value, setValue] = useState<Step8Willingness>(person.willingness);
  const who = person.who.trim() || "this person";
  return (
    <div className="flex min-h-dvh flex-col pb-8">
      {header(who, { back: onBack, saved: true })}
      <div className="animate-fade-rise flex flex-1 flex-col gap-3.5 px-6 pt-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
            Step Eight · Willingness
          </p>
          <h1 className="mt-1 text-xl font-semibold text-foreground">
            Where are you with {who} today?
          </h1>
        </div>
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          Move it whenever it moves. Nothing is recorded about how long it took, and nothing counts
          down.
        </p>
        <WillPicker value={value} onChange={setValue} />
        <Note icon={HeartHandshake}>
          Not willing yet is an honest answer, and it's the one most people give about someone. It
          stays on the list exactly as it is until something changes.
        </Note>
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
            Earlier
          </p>
          <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
            You've had this at {WILL[person.willingness].label} since {shortDate(since)}. That's all
            this note is — no streak, no nudge.
          </p>
        </div>
        <div className="mt-auto flex flex-col gap-2 pt-4">
          <Button size="xl" fullWidth disabled={busy} onClick={() => onSave(value)}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : "Save"}
          </Button>
          <Button variant="ghost" fullWidth onClick={onBack}>
            Leave It As It Is
          </Button>
        </div>
      </div>
    </div>
  );
}
